<script>
import { mapState, mapActions } from 'vuex';
import TransportationTableInputs from '../../components/transportation/TransportationTableInputs';
import TransportationPersonCategoryAdd from '../../components/transportation/TransportationPersonCategoryAdd';
import TransportationPersonCategoryEdit from '../../components/transportation/TransportationPersonCategoryEdit';
import ConfirmDialog from '../../components/TwConfirmDialog';

export default {
  name: 'TransportationPersonCategories',
  components: {
    TransportationTableInputs,
    TransportationPersonCategoryAdd,
    TransportationPersonCategoryEdit,
    ConfirmDialog,
  },
  computed: {
    ...mapState({
      statePagination: state => state.transportation.personCategories.pagination,
      personCategory: state => state.transportation.personCategories.personCategory,
      personCategories: state => state.transportation.personCategories.items,
      itemsError: state => state.transportation.personCategories.itemsError,
      itemsLoading: state => state.transportation.personCategories.itemsLoading,
      search: state => state.transportation.personCategories.search,
      saveError: state => state.transportation.personCategories.saveError,
      saveLoading: state => state.transportation.personCategories.saveLoading,
      lastSupplier: state => state.transportation.personCategories.supplier,
      supplier: state => state.transportation.suppliers.supplier,
      suppliers: state => state.transportation.suppliers.items,
    }),
  },
  methods: {
    ...mapActions({
      setPersonCategorySearch: 'transportation/setPersonCategorySearch',
      getPersonCategoriesBySupplier: 'transportation/getPersonCategoriesBySupplier',
      updatePagination: 'transportation/updatePersonCategoryPagination',
      setActivePersonCategory: 'transportation/setActivePersonCategory',
    }),
    changePage(page) {
      this.pagination.page = page;
    },
    loadPersonCategoriesBySupplier(force = false) {
      if (force || this.supplier !== this.lastSupplier) {
        this.getPersonCategoriesBySupplier()
          .then(() => {
            this.pagination.totalItems = this.personCategories.length;
          });
      }
    },
    openAddPersonCategory() {
      this.addShow = true;
    },
    closeAddConfirm(force = false) {
      if (!force) {
        this.$refs.addConfirm.open();
      } else {
        this.addShow = false;
      }
    },
    openEditPersonCategory(personCategory) {
      if (!this.personCategory || this.personCategory.id !== personCategory.id) {
        this.setActivePersonCategory(personCategory);
      }
      this.editShow = true;
    },
    closeEditConfirm(force = false) {
      if (this.changesMade && !force) {
        this.$refs.confirm.open();
      } else {
        this.editShow = false;
      }
    },
  },
  data() {
    return {
      headers: [
        { text: '#', sortable: false, width: '30px', class: 'px-2', value: '' },
        { text: 'name', class: 'px-2', value: 'name' },
        { text: 'Age from', class: 'px-2', value: 'fromAge' },
        { text: 'Age to', class: 'px-2', value: 'toAge' },
        { text: 'External ref.', class: 'px-2', value: 'externalRef' },
        { text: 'Active', width: '70px', class: 'px-2', value: 'active' },
        { text: 'Actions', width: '70px', sortable: false, class: 'px-2', value: '' },
      ],
      pagination: {},
      addShow: false,
      editShow: false,
      changesMade: false,
      confirm: {
        title: 'Are you sure?',
        description: 'There are changes that are not saved',
      },
    };
  },
  created() {
    this.pagination = Object.assign(this.pagination, this.statePagination);
    this.loadPersonCategoriesBySupplier();
  },
  beforeDestroy() {
    this.updatePagination(this.pagination);
  },
};
</script>

<template>
  <v-container fluid>
    <v-layout>
      <v-flex xs6>
        <transportation-table-inputs
          @change="loadPersonCategoriesBySupplier"
          @search="setPersonCategorySearch"
          :disabled="itemsLoading"
          :search="search"
        />
      </v-flex>
      <v-flex xs3 offset-xs3 class="text-xs-right">
        <v-btn
          @click="openAddPersonCategory"
          :disabled="!supplier"
          color="primary"
        >
          Add new category
        </v-btn>
      </v-flex>
    </v-layout>
    <v-layout mt-3 row>
      <v-flex>
        <v-data-table
          :items="personCategories"
          :headers="headers"
          :loading="itemsLoading"
          :pagination.sync="pagination"
          :search="search"
          no-results-text="No results found"
          no-data-text="No person categories were found for the supplier"
          hide-actions
          must-sort
        >
          <template slot="items" slot-scope="props">
            <tr
              :class="{ 'blue lighten-5': personCategory && props.item.id === personCategory.id }"
            >
              <td class="px-2 pr-4">{{ props.item.id }}</td>
              <td class="px-2">{{ props.item.name }}</td>
              <td class="px-2">{{ props.item.fromAge }}</td>
              <td class="px-2">{{ props.item.toAge }}</td>
              <td class="px-2">{{ props.item.externalRef }}</td>
              <td class="px-2">
                <v-icon v-if="props.item.active" color="green">done</v-icon>
                <v-icon v-else color="red">close</v-icon>
              </td>
              <td class="px-2">
                <v-icon @click="openEditPersonCategory(props.item)" class="mr-2" small>
                  edit
                </v-icon>
              </td>
            </tr>
          </template>
          <template slot="no-data">
            <div v-if="itemsError">
              <v-alert :value="true" icon="warning">
                An error occurred while loading.
                <span @click="loadPersonCategoriesBySupplier(true)" class="refresh-text" >
                  {{ $t('inputs.misc.tryAgain') }}
                </span>
              </v-alert>
            </div>
            <div v-else-if="!supplier" class="text-xs-center">
              <span>Choose a supplier to load person categories</span>
            </div>
            <div v-else-if="!itemsLoading" class="text-xs-center">
              <span>No person categories were found for the supplier</span>
            </div>
            <div v-else class="text-xs-center">
              <!--Show nothing when loading-->
            </div>
          </template>
        </v-data-table>
      </v-flex>

      <v-dialog
        v-model="editShow"
        @keydown.esc="editShow = false"
        max-width="600px"
        no-click-animation
        persistent
      >
        <transportation-person-category-edit
          v-model="changesMade"
          @close="closeEditConfirm"
          :shown="editShow"
        />
      </v-dialog>

      <v-dialog
        v-model="addShow"
        @keydown.esc="addShow = false"
        max-width="600px"
        no-click-animation
        persistent
      >
        <transportation-person-category-add
          @close="closeAddConfirm"
          :shown="addShow"
        />
      </v-dialog>

      <confirm-dialog
        @confirm="addShow = false"
        title="Cancel add person category"
        description="Are you sure you want to cancel adding a new person category?"
        ref="addConfirm"
      />

      <confirm-dialog
        @confirm="editShow = false"
        :title="confirm.title"
        :description="confirm.description"
        ref="confirm"
      />
    </v-layout>

    <v-layout v-if="personCategories" justify-center mt-3 row>
      <v-flex shrink>
        <v-pagination
          @input="changePage"
          :length="Math.ceil(pagination.totalItems / pagination.rowsPerPage)"
          :value="pagination.page"
          total-visible="10"
        >
        </v-pagination>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style type="scss">
  .v-messages {
    min-height: 14px !important;
  }
</style>



// WEBPACK FOOTER //
// src/pages/transportation/TransportationPersonCategories.vue